import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {

    ArrowDown,
    ArrowRightCircle,

} from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import WhatsAppCommunityBanner from '../components/utilities/WhatsAppCommunityBanner';

const GenderBasedViolencePage: React.FC = () => {
    const [currentText, setCurrentText] = useState("Empowerment");
    const textCycle = ["Empowerment", "Healing", "Understanding", "Support"];

    const navigate = useNavigate();

    useEffect(() => {
        const interval = setInterval(() => {
            const currentIndex = textCycle.indexOf(currentText);
            const nextIndex = (currentIndex + 1) % textCycle.length;
            setCurrentText(textCycle[nextIndex]);
        }, 2000);

        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, [currentText]);


    const identityCards = [
        {
            title: "Cognitive Behavioral Therapy (CBT) / Trauma-Focused CBT (TF-CBT)",
            description: "In this type of therapy, people look at the connection between their thoughts, feelings, and actions. For survivors of violence, it can help challenge negative thoughts like blaming themselves for what happened, or feeling shame or intense fear. TF-CBT is a version often used with young people and their caregivers, guiding them through understanding trauma, learning coping skills, and safely processing memories of the event."
        },
        {
            title: "Narrative Therapy",
            description: "This approach sees people as separate from their problems. It works like a partnership, helping someone tell their story in a way that highlights their strengths and allows them to see new possibilities for their future, rather than letting the problem define them. One study showed it was effective for women who had experienced domestic violence.",

        },
        {
            title: "Solution-Focused Brief Therapy (SFBT)",
            description: "As the name suggests, this therapy focuses on solutions instead of dwelling on the problems or the past. It helps people identify what they want their future to look like and build on the skills and strengths they already have to reach those goals.",
        },
        {
            title: "Feminist Counselling",
            description: "This approach understands that GBV is often connected to larger societal issues of unequal power between genders. It helps survivors recognize this is not their fault and empowers them by focusing on their rights and strength."
        }
    ];


    return (
        <>
            <Helmet>
                {/* Basic Meta Tags */}
                <title>Therapy for Gender-Based Violence Survivors In India</title>
                <meta name="description" content="Find culturally-informed psychologist for Gender-Based Violence survivors in India. Trauma-informed
counselling for domestic violence. Get Help Now." />
                {/* Sensitive Content Warning */}
                <meta name="rating" content="mature" />

                {/* Robots Instruction */}
                <meta name="robots" content="index, follow" />

                {/* Open Graph / Social Media Tags */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Gender-Based Violence Support - Healing, Understanding, Empowerment" />
                <meta
                    property="og:description"
                    content="Find culturally-informed psychologist for Gender-Based Violence survivors in India. Trauma-informed
counselling for domestic violence. Get Help Now."
                />
                <meta property="og:url" content="https://grehpraveshtherapy.com/gender-based-violence" />
                <meta property="og:image" content="/images/violence-support.png" />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Survivor Support - Pathways to Healing" />
                <meta
                    name="twitter:description"
                    content="Trauma-informed care for survivors of gender-based violence. Compassionate support that centers your strength and resilience."
                />
                <meta name="twitter:image" content="/images/violence-support.png" />

                {/* Keywords */}
                <meta
                    name="keywords"
                    content="gender-based violence support, survivor counseling, trauma recovery, empowerment, healing from violence, survivor-centered care, emotional support"
                />

                {/* Structured JSON-LD Schema Markup */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "HealthAndWellnessProfessional",
                        "name": "Gender-Based Violence Support Services",
                        "description": "Find culturally-informed psychologist for Gender-Based Violence survivors in India. Trauma-informed counselling for domestic violence. Get Help Now.",
                        "offers": {
                            "@type": "Offer",
                            "description": "Professional, survivor-centered counseling and support services"
                        },
                        "serviceType": [
                            "Trauma Recovery",
                            "Survivor Support",
                            "Gender-Based Violence Counseling"
                        ],
                        "areaServed": {
                            "@type": "GeoShape",
                            "additionalType": "Service Area"
                        },
                        "hasCredential": {
                            "@type": "EducationalOccupationalCredential",
                            "name": "Trauma-Informed Counseling Professionals"
                        },
                        "additionalType": [
                            "Survivor Empowerment",
                            "Trauma Counseling",
                            "Emotional Wellness"
                        ]
                    })}
                </script>
            </Helmet>
            <div className="bg-white">
                {/* Landing Section */}
                <section className="relative min-h-screen flex flex-col md:flex-row overflow-hidden bg-gradient-to-br from-softwhite to-softwhite">
                    {/* Background layers */}
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-rose-800 to-rose-300 rounded-br-[100%] z-0 opacity-50"></div>
                    <div className="absolute top-0 right-0 w-full h-full bg-contain bg-no-repeat bg-right-top opacity-10 md:opacity-20 z-0"
                        style={{ backgroundImage: "url('/images/violence-support.png')" }}
                    ></div>

                    <div className="w-full md:w-7/12 z-10 flex items-center text-center mt-28 md:mt-0 text-center px-6 md:pl-24 md:pr-0 relative">
                        <div className="max-w-2xl w-full space-y-6 mt-32 md:mt-20">



                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="text-3xl md:text-4xl text-white font-semibold md:font-bold"
                            >
                                Therapy for
                                Gender-Based Violence Survivors

                            </motion.h1>

                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 1.5 }}
                                className="hidden md:block text-white/90 text-md max-w-xl"
                            >
                                Gender-Based Violence (GBV) is violence unleashed just because to someone is a boy, girl, man, or
                                woman. Rooted in unequal power between genders, it&#39;s considered a really important global health
                                issue by groups like the World Health Organization (WHO).

                            </motion.p>

                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 1.5 }}
                                className="hidden md:block text-white/90 text-md max-w-xl"
                            >
                                One of the most common types of GBV is domestic violence (DV), which happens within families or
                                relationships. Anyone can be a victim of gender-based violence, but it disproportionately affects
                                women and girls. Therefore, it’s observed more commonly that women seek a psychologist for
                                domestic violence survivors in India.

                            </motion.p>

                            <motion.div
                                className="pt-8 space-y-6"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.7 }}
                            >
                                <button
                                    className="bg-white text-rose-700 flex items-center justify-center text-center px-6 py-3 text-sm md:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all w-full md:w-auto mx-auto"
                                    onClick={() => navigate('/book-appointment')}
                                >
                                    Book Appointment
                                    <ArrowRightCircle className="ml-3 w-7 h-7 text-rose-700" />
                                </button>
                                <motion.div
                                    className="block md:hidden w-full h-96 md:h-full"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.9, delay: 3 }}
                                >
                                    <img
                                        src="/images/violence-support.png"
                                        alt="violence support"
                                        className="w-full h-full object-cover rounded-xl opacity-80"
                                    />
                                </motion.div>
                            </motion.div>
                        </div>
                    </div>
                    <motion.div
                        className="hidden md:block absolute top-0 right-0 w-5/12 h-full z-0"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.9, delay: 3 }}
                    >
                        <img
                            src="/images/violence-support.png"
                            alt="violence support"
                            className="absolute inset-0 w-full h-full object-cover opacity-70"
                        />
                    </motion.div>
                </section>

                <div className="text-center py-4 bg-white">
                    <ArrowDown
                        className="animate-bounce mx-auto text-rose-600"
                        size={32}
                    />
                </div>

                <div className="container mx-auto  space-y-16">
                    <section className="max-w-6xl mx-auto">
                        <div className="text-center mb-8 lg:mb-12">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold text-pink-800 mb-6 leading-tight">
                                How Gender Violence Impacts Survivors?
                            </h2>
                        </div>

                        <div className="space-y-6 lg:space-y-8 max-w-4xl mx-auto">
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 lg:p-8">
                                <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                                    Violence has a severe impact on a person's mental health. Mental health isn't just about feeling
                                    happy; it's about how you think, feel, and handle life. Violence based on gender can really shake
                                    someone up from inside.
                                </p>
                            </div>

                            <div className="grid gap-6 lg:gap-8 md:grid-cols-2">
                                <div className="bg-white rounded-xl p-6 lg:p-8 shadow-lg border border-gray-100">
                                    <h3 className="text-lg lg:text-xl font-semibold text-gray-800 mb-4">Mental Health Impact</h3>
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                        Violence, especially domestic violence, can lead to many difficult feelings and problems with thinking
                                        and feeling, like trouble sleeping, feeling really anxious or worried, feeling sad or depressed, having
                                        panic attacks, or even having something called PTSD (Post-Traumatic Stress Disorder).
                                    </p>
                                </div>

                                <div className="bg-white rounded-xl p-6 lg:p-8 shadow-lg border border-gray-100">
                                    <h3 className="text-lg lg:text-xl font-semibold text-gray-800 mb-4">PTSD Symptoms</h3>
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                        PTSD can mean having nightmares or flashbacks about what happened, feeling constantly on edge,
                                        or avoiding things that remind you of the violence. Women who have experienced violence are
                                        much more likely than men to have these kinds of mental health challenges.
                                    </p>
                                </div>
                            </div>

                            <div className="bg-red-50 border-l-4 border-red-400 rounded-r-xl p-6 lg:p-8">
                                <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                                    <span className="font-semibold">Important fact:</span> Half of women who experience violence also have had a mental health diagnosis. The risk of
                                    experiencing depression, PTSD, substance use issues, or even thinking about suicide is much higher
                                    for women who have faced violence.
                                </p>
                            </div>
                        </div>
                    </section>

                    <section className="max-w-6xl mx-auto">
                        <div className="text-center mb-8 lg:mb-12">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold text-gray-800 mb-6 leading-tight">
                                How Therapists Help Survivors?
                            </h2>
                        </div>

                        <div className="space-y-6 lg:space-y-8 max-w-4xl mx-auto">
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 lg:p-8">
                                <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed mb-4">
                                    GBV is a major cause of distress and psychological problems. Therapy helps people understand and
                                    cope with these difficult feelings and experiences caused by the violence.
                                </p>
                                <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                                    It's a space where someone can talk about what happened without being judged, and learn ways to
                                    heal and feel safer and stronger. The goal of therapy is to help survivors work through the impacts of
                                    the trauma, improve their mental well-being, and support their healing and personal growth.
                                </p>
                            </div>

                            <div className="grid gap-6 lg:gap-8 md:grid-cols-2">
                                <div className="bg-white rounded-xl p-6 lg:p-8 shadow-lg border border-gray-100">
                                    <h3 className="text-lg lg:text-xl font-semibold text-green-800 mb-4">Counselling Support</h3>
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                        Many places offer counselling or psychological support, where you talk one-on-one with a trained person
                                        or sometimes in a group with others who have similar experiences. A key part of this is "listening without judgment",
                                        showing you understand how they feel (empathy), and letting them know their feelings are normal (validation).
                                    </p>
                                </div>

                                <div className="bg-white rounded-xl p-6 lg:p-8 shadow-lg border border-gray-100">
                                    <h3 className="text-lg lg:text-xl font-semibold text-green-800 mb-4">Trauma-Informed Care</h3>
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                        This means the counsellors and staff helping you have been trained to understand how trauma affects people.
                                        They know that experiences of violence can impact how someone feels and behaves, and they make sure the
                                        environment feels safe and supportive. They focus on helping you feel safe and in control.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                    <section className="max-w-6xl mx-auto">
                        <div className="text-center mb-8 lg:mb-12">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold text-pink-800 mb-6 leading-tight">
                                Types of Therapy
                            </h2>
                        </div>

                        <div className="grid gap-6 lg:gap-8 sm:grid-cols-2 lg:grid-cols-3">
                            {identityCards.map((card, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.2 }}
                                    className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all hover:scale-105 hover:shadow-xl"
                                >
                                    <div className="p-6 lg:p-8">
                                        <h3 className="text-xl lg:text-2xl font-semibold text-gray-800 text-center mb-4">
                                            {card.title}
                                        </h3>
                                        <p className="text-center text-base lg:text-lg text-gray-600 leading-relaxed">
                                            {card.description}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </section>

                    <section className="max-w-6xl mx-auto">
                        <div className="text-center mb-8 lg:mb-12">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold text-gray-800 mb-6 leading-tight">
                                Cultural Complications
                            </h2>
                        </div>

                        <div className="space-y-6 lg:space-y-8 max-w-4xl mx-auto">
                            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 lg:p-8">
                                <p className="text-base sm:text-lg lg:text-xl text-yellow-700 leading-relaxed">
                                    In a country like India, understanding the cultural context becomes important. Domestic violence is a
                                    big issue in India, often involving violence from husbands and relatives in the home.
                                </p>
                            </div>

                            <div className="grid gap-6 lg:gap-8 md:grid-cols-2">
                                <div className="bg-white rounded-xl p-6 lg:p-8 shadow-lg border border-gray-100">
                                    <h3 className="text-lg lg:text-xl font-semibold text-amber-800 mb-4">Cultural Barriers</h3>
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                        Cultural beliefs about gender roles, family privacy, and expectations can make things complicated.
                                        Sometimes, people believe violence is justified if a woman doesn't follow traditional rules.
                                    </p>
                                </div>

                                <div className="bg-white rounded-xl p-6 lg:p-8 shadow-lg border border-gray-100">
                                    <h3 className="text-lg lg:text-xl font-semibold text-amber-800 mb-4">Family Dynamics</h3>
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                        There can be pressure to keep family problems private to protect the family's reputation. Because
                                        family is so important in Indian culture, women might first turn to informal support from relatives.
                                    </p>
                                </div>
                            </div>

                            <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-r-xl p-6 lg:p-8">
                                <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                                    Therapists working in India need to be culturally sensitive and understand these complexities.
                                    Leaving aside judgments or stereotypes of people based on their cultural background can really go a
                                    long way. They might need to work with the family system, recognizing that families can be both a
                                    source of support and conflict.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Approach Section */}
                    <section className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl p-6 lg:p-12">
                        <div className="text-center max-w-4xl mx-auto">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold text-gray-800 mb-8 lg:mb-12 leading-tight">
                                Therapists Who Support You, Get Support From Us
                            </h2>

                            <div className="space-y-6 lg:space-y-8 text-left">
                                <div className="bg-white rounded-xl p-6 lg:p-8 shadow-sm">
                                    <h3 className="text-lg lg:text-xl font-semibold text-gray-800 mb-4">Vicarious Trauma</h3>
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                        It's also important to take care of the people who are giving the help. Hearing stories of trauma can
                                        be emotionally difficult for counsellors and staff. This can lead to something called vicarious trauma,
                                        where they experience negative effects from being exposed to the suffering of others.
                                    </p>
                                </div>

                                <div className="bg-white rounded-xl p-6 lg:p-8 shadow-sm">
                                    <h3 className="text-lg lg:text-xl font-semibold text-gray-800 mb-4">Our Approach at Greh Pravesh</h3>
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                        Taking care of both the survivor and the helper is part of a complete approach to dealing with the
                                        impacts of GBV. At Greh Pravesh, our therapists support each other and collaborate so they
                                        don't have to deal with it in isolation (always with your privacy in mind).
                                    </p>
                                </div>

                                <div className="bg-green-50 rounded-xl p-6 lg:p-8 border border-green-200">
                                    <h3 className="text-lg lg:text-xl font-semibold text-gray-800 mb-4">Building Resilience</h3>
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed mb-4">
                                        It is important for helpers to receive support, training, and opportunities to talk about their
                                        experiences. They can also experience vicarious resilience, which is the positive feeling and strength
                                        they gain from witnessing the healing and resilience of the survivors they help.
                                    </p>
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                        This support needs to be sensitive to cultural factors, especially in places like India, and provided by
                                        well-trained and supported professionals. Seeking help is a sign of courage and strength, not
                                        weakness.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>



                    <WhatsAppCommunityBanner />
                    {/* Call to Action */}
                    <section className="bg-rose-600 bg-opacity-10 rounded-xl p-12 text-center">
                        <h2 className="text-4xl font-semibold text-gray-800 mb-6">
                            Your Journey, Your Strength
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
                            Every survivor's experience is unique. We're here to provide compassionate,
                            non-judgmental support that honors your individual path to healing and empowerment.
                        </p>
                        <div className="flex flex-col md:flex-row justify-center space-x-4 md:space-x-4 space-y-4 md:space-y-0 w-full max-w-md mx-auto">
                            <Link
                                className="bg-rose-600 text-white px-8 py-3 rounded-lg hover:bg-opacity-90 transition-colors font-raleway-regular"
                                to="/book-appointment"
                            >
                                Schedule Consultation
                            </Link>

                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default GenderBasedViolencePage;